import { Sheet } from '@mui/joy';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Button,
  Divider,
  Typography,
  Box,
  MenuItem,
  TextField,
  Tooltip,
  ButtonGroup,
  IconButton
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import AttachReceivedSvg from 'src/components/AttachReceivedSvg';
import AttachSendSvg from 'src/components/AttachSendSvg';
import CIconButton from 'src/components/CIconButton';
import InputLine from 'src/components/CustomInputLine';
import Iconify from 'src/components/Iconify';
import { useAffectation_v4Context } from 'src/contexts/Affectation_v4Context';
import useAuth from 'src/hooks/useAuth';
import { Affectation_Setting_model, Affectation_v4_Type } from 'src/models/Affectation_v4_type';
import OrganigrammeItem from './OrganigrammeItem';
import { AFFECT_ANNOTATION, AFFECT_OPERATION_TYPE, AFFECT_PRIORITY } from 'src/constants/affectation';
import { getFileThumbSecond } from 'src/utils/getFileFormat';
import { compareArraysOfObjects } from 'src/utils/changeOnObject';
import { uniq, values, add } from 'lodash';
import { serverTime } from 'src/utils/serverTime';
import { dispatch } from 'src/redux/store';
import { updateAffectation } from 'src/redux/slices/affectation_v4';
import { AffectLineDue, RejectMessage, NoteViewer } from './liste/AffectationLine';
import AffectationRappel from './rappel/AffectationRappel';
import ActionModal from 'src/components/ActionModal';
import { useToggleV2 } from 'src/hooks/useToggle';
import CustomFileView from 'src/components/CustomFileView';
import LoadingScreen from 'src/components/LoadingScreen';
import { useSnackbar } from 'notistack';

/**
 *
 * @param {{open: boolean, affectation: Affectation_v4_Type, onClose: ()=>void}} props
 * @returns
 */
export default function AffectationDetails({ open, affectation, onClose, disabled }) {
  const { user } = useAuth();
  const { settings: { organigramme = [] } = Affectation_Setting_model } = useAffectation_v4Context();
  const [saving, setSaving] = useState(false);
  const [_saveWithoutDocument, _setSaveWithoutDocument] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saveWithoutUpdate, displaySaveWithoutUpdate, hidesaveWithoutUpdate] = useToggleV2();
  const snackbar = useSnackbar();

  const [visible, display, hide] = useToggleV2();
  const [noteModal, displayNote, hideNote] = useToggleV2();
  const [saveWithoutDocument, displaySaveWithoutDocument, hideSaveWithoutDocument] = useToggleV2();
  const [notSavedFiles, setNotSavedFiles] = useState([]);
  const assigne = useMemo(() => {
    return affectation.assignationOrganigramme;
  }, [affectation.assignationOrganigramme]);

  const myLevel = useMemo(() => {
    const position = organigramme.findIndex((one) => one.users.find((_userId) => _userId === user.uid));
    if (!assigne.length) {
      const _ass = organigramme.map((org) => ({ ...org, users: [] }));
      if (position !== -1) {
        return {
          assigne: _ass,
          position
        };
      }
      return { assigne: _ass, position: 0 };
    }
    return { assigne, position };
  }, [assigne, organigramme, user.uid]);

  const noteViewRef = useRef();

  //console.log(disabled)

  const formik = useFormik({
    initialValues: affectation,
    onSubmit: async (values) => {
      setIsUploading(true);

      const oldAssigne = affectation.assignationOrganigramme;
      const newOrganigramme = values.assignationOrganigramme;
      const documentsIsUpdate = values.attachments === affectation.attachments;

      const modifiedInput = () => {
        return Object.keys(values).reduce((acc, key) => {
          if (values[key] !== affectation[key]) {
            acc[key] = values[key];
          }
          return acc;
        }, {});
      };

      //console.log(modifiedInput());

      let toDoIds = affectation.toDoIds;

      let toReviewIds = uniq([...affectation.toReviewIds, user.uid]);

      //check change user on assigne organigramme and add to toDoIds
      newOrganigramme.forEach((level, index) => {
        const oldLevel = oldAssigne[index];

        const { added } = compareArraysOfObjects(oldLevel?.users || [], level.users);

        toReviewIds = toReviewIds.filter((oneId) => !added.some((one) => one === oneId));

        toDoIds = uniq([...toDoIds, ...added]);
      });

      toReviewIds.forEach((_id) => (toDoIds = toDoIds.filter((_oneId) => _oneId !== _id)));
      const onLowerLevelIsAssigned = values.assignationOrganigramme.map((one, index) => {
        //return only lower level users
        return index > myLevel.position ? one.users : [];
      });
      // au moin un niveau est assigné
      const atlastOneLevelIsAssigned =
        values.assignationOrganigramme.map((one) => one.users.length).reduce((a, b) => a + b, 0) > 0;
      let toSave = {
        ...values,
        updated: {
          by: user.uid,
          at: serverTime()
        }
      };
      // deplacer si
      // une annotation est ajouté et une assignation est faite sur au moins un niveau
      // une affectation est marqué comme terminé
      // les propriétés d'une affectation sont modifiés et au moins un niveau est assigné et l'annotation est ajouté
      if (
        (values.annotation && values.annotation.length > 0 && atlastOneLevelIsAssigned) ||
        values.completed === true ||
        (modifiedInput().length > 0 && values.annotation && values.annotation.length > 0 && atlastOneLevelIsAssigned)
      ) {
        toSave = {
          ...toSave,
          toDoIds,
          toReviewIds
        };
      }

      dispatch(
        updateAffectation({
          newAffectation: toSave,
          oldAffectation: affectation,
          callback: () => {
            snackbar.enqueueSnackbar('Affectation enregistrée avec succès', { variant: 'success' });
          },
          onError: () => {
            snackbar.enqueueSnackbar("Erreur lors de l'enregistrement de l'affectation", { variant: 'error' });
          }
        })
      );
      _setSaveWithoutDocument(false);

      onClose();

      setIsUploading(false);
    }
  });

  const { handleSubmit, setFieldValue, errors, values } = formik;

  const handleSave = () => {
    const newAff = { ...values };
    //map affectation propreties
    const isUnchanged = Object.keys(values).every((key) => {
      // compare all properties except attachments
      //console.log({ key, value: values[key], affectation: affectation[key] });
      if (values[key] === affectation[key] || key === 'priority') {
        return true;
      }
    });
    //console.log({ isUnchanged, newAff, affectation })
    if (isUnchanged) {
      displaySaveWithoutUpdate();
      return;
    }
    //si la personne est dans le dernier niveau et qu'il n'a pas de document
    else if (values.attachments === affectation.attachments && !_saveWithoutDocument) {
      displaySaveWithoutDocument();
      return;
    }
    handleSubmit();
  };

  const handleSaveWithoutDocument = () => {
    _setSaveWithoutDocument(true);
    handleSubmit();
  };

  const handleSaveWithoutUpdate = () => {
    _setSaveWithoutDocument(false);
    handleSubmit();
  };

  const addUniqueFile = (field, newFiles) => {
    let existingFiles = values[field];
    if (!Array.isArray(existingFiles)) {
      existingFiles = [];
    }
    const uniqueFiles = [...existingFiles];

    newFiles.forEach((newFile) => {
      if (!existingFiles.some((file) => file.url === newFile.url)) {
        uniqueFiles.push(newFile);
      }
    });

    setFieldValue(field, uniqueFiles);
  };

  //console.log(values?.attachments?.received.length, values?.attachments?.send.length);

  const handleChangeDone = () => {
    setFieldValue('completed', true);
    setFieldValue('completedBy', user.uid);
    let val = { ...values.validation, state: false, by: null, isReject: false };
    setFieldValue('validation', val);
    //
    handleSubmit();
  };

  const handleChangeNotDone = () => {
    setFieldValue('completed', false);
    setFieldValue('completedBy', user.uid);
    handleSubmit();
  };

  const handleValidate = () => {
    setFieldValue('validation', { ...values?.validation, state: true, by: user.uid });
    handleSubmit();
  };

  const canDelete = useMemo(() => {
    // seul le créateur peut supprimer
    return user.uid === affectation.createdBy;
  }, [affectation.createdBy, user.uid]);

  const handleReject = (note) => {
    let val = { ...values.validation, state: false, by: null, isReject: true };
    if (note) {
      val = {
        ...val,
        rejectNote: [...(values?.validation?.rejectNote || []), { by: user.uid, note, at: serverTime() }]
      };
    }
    setFieldValue('validation', val);
    setFieldValue('completed', false);
    setFieldValue('completedBy', null);
    handleChangeNotDone();
  };

  const levelDown = useMemo(() => {
    const myposition = organigramme?.findIndex((one) => one.users.find((_userId) => _userId === user.uid));
    const completedByposition = organigramme?.findIndex((one) =>
      one.users.find((_userId) => _userId === values.completedBy)
    );
    if (completedByposition !== -1 && myposition !== -1) {
      return myposition > completedByposition;
    }

    return false;
  }, [values.completedBy, organigramme, user.uid]);

  const blobFiles = useMemo(() => {
    return values.attachments.send.filter((one) => one.url.startsWith('blob:'));
  }, [values.attachments.send]);

  const uploadedFiles = useMemo(() => {
    return values?.attachments?.send.filter((one) => !one.url.startsWith('blob:'));
  }, [values.attachments.send]);

  //console.log(values.attachments.send)

  return (
    <>
      <FormikProvider value={formik}>
        <Dialog
          maxWidth="lg"
          fullWidth
          open={Boolean(open)}
          onClose={onClose}
          PaperProps={{ sx: { border: 'none', boxShadow: 'none', bgcolor: 'transparent', p: 2 } }}
          scroll="paper"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'center',

              pb: 2,
              bgcolor: 'background.paper',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20
            }}
          >
            <Stack width={1} direction="row">
              <Stack width={0.4} justifyContent="center">
                <Typography fontWeight="bold" variant="h4">
                  Assignation
                </Typography>
              </Stack>

              <Stack width={0.6} spacing={3} direction="row" justifyContent="end">
                <Stack direction={'row'} spacing={2}>
                  {values.completed ? (
                    values.completedBy === user.uid || levelDown ? (
                      <Tooltip title="Marquer comme non terminée" arrow>
                        <Button
                          variant="contained"
                          color="success"
                          sx={{ py: 1.5 }}
                          onClick={handleChangeNotDone}
                          disabled={disabled}
                        >
                          Terminée
                        </Button>
                      </Tooltip>
                    ) : !values.validation.state ? (
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          color="error"
                          sx={{ py: 1.5 }}
                          endIcon={<Iconify icon="uiw:dislike-o" />}
                          onClick={display}
                          disabled={disabled}
                        >
                          Rejeter
                        </Button>

                        <Button
                          variant="contained"
                          color="success"
                          sx={{ py: 1.5 }}
                          endIcon={<Iconify icon="uiw:like-o" />}
                          onClick={handleValidate}
                          disabled={disabled}
                        >
                          Accepter
                        </Button>
                      </ButtonGroup>
                    ) : (
                      <Tooltip title="Marquer comme non terminée" arrow>
                        <Button
                          variant="contained"
                          color="success"
                          sx={{ py: 1.5 }}
                          onClick={handleChangeNotDone}
                          disabled={disabled}
                        >
                          Terminée
                        </Button>
                      </Tooltip>
                    )
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        sx={{ py: 1.5 }}
                        endIcon={<Iconify icon="ph:check-bold" />}
                        onClick={handleChangeDone}
                        disabled={disabled}
                      >
                        Marquer comme terminée
                      </Button>
                      {affectation.validation.isReject && (
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{ py: 1.5 }}
                          startIcon={<Iconify icon="ph:note-light" className="clignotant" />}
                          onClick={displayNote}
                          ref={noteViewRef}
                          disabled={disabled}
                        >
                          Note de rejet
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>

                <Button variant="contained" color="primary" sx={{ py: 1.5 }} onClick={handleSave} disabled={disabled}>
                  ENREGISTRER
                </Button>
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 3,
              position: 'relative',
              bgcolor: 'background.paper',
              borderBottomLeftRadius: 20,
              borderBottomRightRadius: 20
            }}
          >
            <Form noValidate onSubmit={handleSubmit}>
              <Stack width={1} direction="row" justifyContent="space-between" spacing={3}>
                <Stack width={0.4}>
                  <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                    <Stack spacing={2}>
                      <AssigneView
                        user={user}
                        organigramme={organigramme}
                        assigne={affectation.assignationOrganigramme}
                        toDoIds={affectation.toDoIds}
                        onChange={setFieldValue}
                        disabled={disabled}
                      />
                    </Stack>
                  </Sheet>
                </Stack>
                <Stack width={0.6}>
                  <Sheet>
                    <Stack width={1} spacing={2}>
                      <Divider />

                      <Stack position="relative">
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                          <Stack width={1} direction="row" spacing={3}>
                            <InputLine
                              noWritable={user.uid !== affectation.createdBy}
                              type="text"
                              label="Référence"
                              value={values.courriel_reference}
                              field="courriel_reference"
                              onChange={setFieldValue}
                              disabled={disabled}
                              sx={{ fontSize: 14 }}
                            />
                            <InputLine
                              disabled={user.uid !== affectation.createdBy || disabled}
                              type="date"
                              label="Date"
                              value={values.courriel_date}
                              field="courriel_date"
                              onChange={setFieldValue}
                              sx={{ fontSize: 14 }}
                            />

                            <InputLine
                              noWritable={user.uid !== affectation.createdBy || disabled}
                              type="text"
                              disabled={user.uid !== affectation.createdBy || disabled}
                              label="Expéditeur"
                              value={values.correspondant.title}
                              field="correspondant.title"
                              onChange={setFieldValue}
                              sx={{ fontSize: 14 }}
                            />
                          </Stack>
                          <Stack width={1} direction="row" spacing={3}>
                            <InputLine
                              type="text"
                              label="Objet"
                              disabled={user.uid !== affectation.createdBy || disabled}
                              value={values.courriel_object}
                              field="courriel_object"
                              onChange={setFieldValue}
                              sx={{ fontSize: 12 }}
                            />
                          </Stack>
                        </Sheet>
                        <Box position="absolute" top={-10} left={20} bgcolor="#FFF" px={1}>
                          <Typography fontWeight="bold" fontSize={15}>
                            COURRIER
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack width={1} direction="row" spacing={3}>
                        <InputLine
                          type="Autocomplete"
                          options={AFFECT_ANNOTATION}
                          label="Annotation"
                          value={affectation.annotation}
                          field="annotation"
                          required
                          onChange={setFieldValue}
                          disabled={disabled}
                          sx={{
                            border: '1px solid #DBDBDC',
                            borderRadius: 1,
                            fontSize: 14
                          }}
                          sxSheet={{
                            height: 55
                          }}
                        />
                      </Stack>

                      <Stack width={1} direction="row" spacing={3}>
                        <InputLine
                          type="select"
                          options={Object.entries(AFFECT_PRIORITY).map(([_, val]) => val)}
                          label="Priorité"
                          value={values.priority}
                          field="priority"
                          onChange={setFieldValue}
                          disabled={disabled}
                          sx={{ fontSize: 14 }}
                        />

                        <InputLine
                          type="select"
                          options={AFFECT_OPERATION_TYPE}
                          label="Type d'opération"
                          value={values.operation_type}
                          field="operation_type"
                          onChange={setFieldValue}
                          disabled={disabled}
                          sx={{ fontSize: 14 }}
                        />
                      </Stack>

                      <Stack width={1} direction="row" spacing={3}>
                        <InputLine
                          type="date"
                          label="Date d'enrégistrement"
                          value={values.save_date}
                          field="save_date"
                          onChange={setFieldValue}
                          disabled={disabled}
                          sx={{ fontSize: 14 }}
                        />
                        <Stack width={1} spacing={0.5}>
                          <Typography fontSize={12}>Échéance Envoyée</Typography>
                          <AffectLineDue
                            vue="input"
                            affect={affectation}
                            onChange={setFieldValue}
                            defaultPosition={1}
                            disabled={disabled}
                          />
                        </Stack>
                        <Stack width={1} spacing={0.5}>
                          <Typography fontSize={12}>Échéance Reçu</Typography>
                          <AffectLineDue
                            vue="input"
                            affect={affectation}
                            onChange={setFieldValue}
                            defaultPosition={0}
                            disabled={true}
                          />
                        </Stack>
                      </Stack>

                      <AffectationRappel affectation={affectation} disabled={disabled} />

                      <Stack width={1} alignItems="center" spacing={3}>
                        <Typography fontSize={13}>Pièces jointes</Typography>
                        <Stack width={1} direction="row" spacing={3} justifyContent="center">
                          <Stack direction="row" alignItems="center">
                            <CustomFileView
                              attachments={values?.attachments?.received}
                              isUploading={false}
                              onRemove={(attachment) => {
                                setFieldValue(
                                  'attachments.received',
                                  values.attachments.received.filter((one) => one.url !== attachment.url)
                                );
                              }}
                              onUpdateFile={null}
                              hideDelete={!canDelete}
                              hideAdd={true}
                              onChange={null}
                              disabled={true}
                            />
                          </Stack>
                          <Sheet
                            variant="soft"
                            sx={{
                              backgroundColor: 'skyblue',
                              height: 100,
                              width: 100,
                              cursor: '',
                              overflow: 'hidden',
                              borderRadius: 'sm',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Stack alignItems="center">
                              <Tooltip
                                title="Pieces jointes initialement attachées au courrier lors de sa création , elles ne peuvent pas etre modifiées"
                                arrow
                              >
                                <IconButton
                                  style={{
                                    position: 'absolute',
                                    top: -3,
                                    right: -3,
                                    color: '#2292fd',
                                    width: 30,
                                    height: 30,
                                    zIndex: 1
                                  }}
                                  aria-label="info"
                                  color="#2292fd"
                                >
                                  <Iconify icon="eva:info-outline" />
                                </IconButton>
                              </Tooltip>
                              <InputLine
                                type="file"
                                label=""
                                disabled={true}
                                value={values.attachments.received}
                                field="attachments.received"
                                sx={{ fontSize: 14 }}
                                onChange={(field, value) => {
                                  setFieldValue(field, uniq([...values.attachments.received, ...value]));
                                }}
                                fileView={
                                  <Stack sx={{ cursor: 'not-allowed' }} disabled>
                                    <AttachReceivedSvg />
                                    <Typography color="info.main" fontSize={10} fontWeight="bold">
                                      Reçue(s)
                                    </Typography>
                                  </Stack>
                                }
                              />
                            </Stack>
                          </Sheet>
                          <Sheet
                            variant="soft"
                            color="success"
                            sx={{
                              height: 100,
                              width: 100,
                              overflow: 'hidden',
                              borderRadius: 'sm',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Stack alignItems="center">
                              <Tooltip title="Pieces jointes ajoutées par l'utilisateur" arrow>
                                <IconButton
                                  style={{
                                    position: 'absolute',
                                    top: -3,
                                    right: -3,
                                    color: '#77b82b',
                                    width: 30,
                                    height: 30,
                                    zIndex: 1
                                  }}
                                  aria-label="info"
                                  color="#77b82b"
                                >
                                  <Iconify icon="eva:info-outline" />
                                </IconButton>
                              </Tooltip>
                              <InputLine
                                type="file"
                                label=""
                                value={values.attachments.send}
                                field="attachments.send"
                                disabled={disabled}
                                onChange={(field, value) =>
                                  setFieldValue(field, uniq([...values.attachments.send, ...value]))
                                }
                                fileView={
                                  <Stack>
                                    <AttachSendSvg />
                                    <Typography color="success.main" fontSize={10} fontWeight="bold">
                                      Envoyée(s)
                                    </Typography>
                                  </Stack>
                                }
                              />
                            </Stack>
                          </Sheet>
                          <Stack direction="row" alignItems="center">
                            <CustomFileView
                              attachments={values.attachments.send}
                              isUploading={false}
                              onRemove={(attachment) => {
                                setFieldValue(
                                  'attachments.send',
                                  values.attachments.send.filter((one) => one.url !== attachment.url)
                                );
                              }}
                              onUpdateFile={null}
                              hideDelete={!canDelete}
                              hideAdd={true}
                              canDeleteBlob
                              onChange={null}
                              disabled={disabled}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Sheet>
                </Stack>
              </Stack>
            </Form>
          </DialogContent>
          <Stack
            direction="row"
            spacing={1}
            p={0.3}
            alignItems="center"
            sx={{
              position: 'absolute',
              top: '0px',
              right: '5px',
              borderRadius: 1,
              backgroundColor: 'transparent'
            }}
          >
            <CIconButton
              noBorder
              disabled={saving}
              title="Fermer"
              onClick={onClose}
              sx={{
                color: '#fff',
                bgcolor: 'red',
                ':hover': { bgcolor: 'red' },
                ':disabled': { bgcolor: 'error.light' }
              }}
            >
              <Iconify icon="eva:close-fill" />
            </CIconButton>
          </Stack>
        </Dialog>
      </FormikProvider>
      {visible && <RejectMessage open={Boolean(visible)} onClose={hide} onReject={handleReject} />}
      {noteModal && (
        <NoteViewer
          open={Boolean(noteModal)}
          onClose={hideNote}
          note={affectation.validation.rejectNote}
          anchor={noteViewRef?.current}
        />
      )}
      {saveWithoutDocument && (
        <SaveWithoutDocument
          open={Boolean(saveWithoutDocument)}
          onClose={hideSaveWithoutDocument}
          onAccept={handleSaveWithoutDocument}
        />
      )}
      {isUploading && <LoadingScreen />}

      {saveWithoutUpdate && (
        <SaveWithoutUpdateModal
          open={Boolean(saveWithoutUpdate)}
          onClose={hidesaveWithoutUpdate}
          onAccept={handleSaveWithoutUpdate}
        />
      )}
    </>
  );
}

/**
 *
 * @param {{
 * user: firebase.default.UserInfo,
 * organigramme: Array<{label:string, users: Array}>,
 * assigne: Array<{label:string, users: Array}>,
 * onChange: (field, value) => void,
 * disabled: boolean
 * }} props
 */
const AssigneView = ({ user, organigramme, assigne, onChange, disabled = false }) => {
  const myLevel = useMemo(() => {
    const position = organigramme.findIndex((one) => one.users.find((_userId) => _userId === user.uid));
    if (!assigne.length) {
      const _ass = organigramme.map((org) => ({ ...org, users: [] }));
      if (position !== -1) {
        return {
          assigne: _ass,
          position
        };
      }
      return { assigne: _ass, position: 0 };
    }
    return { assigne, position };
  }, [assigne, organigramme, user.uid]);

  const [assignationOrganigramme, setAssigneOrganigramme] = useState(myLevel.assigne);

  const handleAssigneTo = (currentLevel, newItem) => {
    const level = currentLevel + myLevel?.position + 1;

    //console.log({ level: level, position: myLevel?.position, newItem });
    setAssigneOrganigramme((old) => {
      let newOrg = [...old];

      if (newOrg[level]) {
        newOrg = newOrg.map((item, index) => {
          if (index === level) {
            return { ...item, ...newItem };
          }
          return item;
        });

        onChange('assignationOrganigramme', [...newOrg]);
        return [...newOrg];
      }

      onChange('assignationOrganigramme', [...newOrg]);
      return [...newOrg];
    });
  };

  return (
    <Stack alignItems="start">
      {assignationOrganigramme.slice(myLevel.position + 1, organigramme.length).map((org, index) => (
        <OrganigrammeItem
          key={org.label + index}
          item={org}
          isLast={index === assignationOrganigramme.length - 1}
          isFirst={index === 0}
          index={index}
          updateLevel={handleAssigneTo}
          assingation
          defaultPosition={myLevel.position}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
};

const SaveWithoutUpdateModal = ({ open, onClose, onAccept }) => {
  return (
    <ActionModal
      title={'Enregistrer'}
      color="warning"
      open={open}
      onClose={onClose}
      moreAction={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>
            Vous n'avez pas apporté de modification à cette affectation. Voulez-vous l'enregistrer quand même ?
          </Typography>
        </Stack>
      }
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button color="warning" variant="contained" onClick={onAccept}>
            Enregistrer
          </Button>
        </Stack>
      }
    />
  );
};

const RejectionModal = ({ open, onClose, onAccept }) => {
  const [hasError, setError] = useState(false);
  const [message, setMessage] = useState('');
  const [isConfirmed, _, __, setConfirm] = useToggleV2();

  const handleAccept = () => {
    if (onAccept && message.length > 0) {
      onAccept(message);
      onClose();
    } else {
      setError(true);
    }
  };

  return (
    <>
      <ActionModal
        title={'Motif du rejet'}
        color="error"
        open={open}
        onClose={onClose}
        moreAction={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={message}
              onChange={(args) => setMessage(args.target.value)}
              placeholder={'Motif...'}
              fullWidth
              multiline
              minRows={4}
              error={hasError}
              helperText={hasError && 'Ce champs est requis'}
            />
          </Stack>
        }
        action={
          <Button color="error" variant="contained" onClick={handleAccept}>
            Rejeter
          </Button>
        }
      />
    </>
  );
};

const SaveWithoutDocument = ({ open, onClose, onAccept }) => {
  return (
    <>
      <ActionModal
        title={'Enregistrer sans pièce jointe'}
        color="warning"
        open={open}
        onClose={onClose}
        moreAction={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>
              Vous êtes sur le point d'enregistrer cette affectation sans y avoir ajouté de pièce jointe. Voulez-vous
            </Typography>
          </Stack>
        }
        action={
          <Button color="warning" variant="contained" onClick={onAccept}>
            Continuer
          </Button>
        }
      />
    </>
  );
};
